import humps from 'humps';
import {
  type ResubmissionLine,
  type ResubmissionRequest,
  type ResubmissionUploadResult, api } from './api';
import type {
  Client,
  CreateClientData,
  Order,
  CreateOrderData,
  OrderItem,
  ItemKind,
  IntakeResult,
  OrderStatus,
  OrderStatusHistoryEntry,
  OrderCost,
  GenerationEstimate,
  Complexidade,
  Prioridade,
  WorkflowSource,
  WorkflowGenerateTaskResponse,
  OrderImage,
  ReferenceType,
  GalleryRejection,
} from '../types/order';
import type { ImageAnnotation } from '../components/orders/ImageAnnotationCanvas';

/**
 * Estado FINAL de um item no confirm-triage (TRI-04). O backend reconcilia:
 * id existente = atualiza, id null = cria (split), item omitido = soft-delete,
 * imagens re-parenteadas por inputImages. A camada api.ts decameliza o corpo
 * (pecaType → peca_type, photosPerProduct → photos_per_product, etc.).
 */
export interface TriageItemInput {
  id: number | null;
  name: string;
  kind: ItemKind;
  pecaType: string | null;
  photosPerProduct: number;
  cenario: string | null;
  modelos: string | null;
  poses: string | null;
  instrucoes: string | null;
  inputImages: { id: number }[];
}

/** Mapeia itens (do servidor ou editados) para o payload de reconciliação. */
export function toTriageInput(items: OrderItem[]): TriageItemInput[] {
  return items.map((item) => ({
    // ids sintéticos de itens novos (não-numéricos) viram null → backend cria.
    id: /^\d+$/.test(String(item.id)) ? Number(item.id) : null,
    name: item.name,
    kind: item.kind,
    // Conjunto não tem "peça" singular (as peças vêm das imagens); nunca envia
    // peca_type mesmo que a sugestão da visão o tenha pré-preenchido.
    pecaType: item.kind === 'conjunto' ? null : item.pecaType ?? null,
    photosPerProduct: item.photosPerProduct,
    cenario: item.cenario ?? null,
    modelos: item.modelos ?? null,
    poses: item.poses ?? null,
    instrucoes: item.instrucoes ?? null,
    inputImages: (item.inputImages ?? []).map((img) => ({ id: Number(img.id) })),
  }));
}

/**
 * Unwraps the API envelope. The backend sometimes wraps payloads in
 * `{ data: ... }` (Laravel Resource) and sometimes returns the raw object.
 */
function unwrap<T>(response: { data: unknown }): T {
  const body = response.data as { data?: T } | T;
  if (body && typeof body === 'object' && 'data' in (body as Record<string, unknown>)) {
    return (body as { data: T }).data;
  }
  return body as T;
}

export interface AdminOrderFilters {
  status?: OrderStatus;
  clientId?: string;
  assigneeId?: string;
  prioridade?: Prioridade;
  needsReview?: boolean;
}

export interface AdminUpdateOrderData {
  status?: OrderStatus;
  titulo?: string;
  complexidade?: Complexidade;
  prioridade?: Prioridade;
  assigneeId?: string;
  /** G3: optional reason for the move, logged in the status history. */
  note?: string;
}

export const OrderService = {
  // ---------- Clients (admin) ----------
  async adminGetClients(): Promise<Client[]> {
    const response = await api.get('/admin/clients');
    return unwrap<Client[]>(response) ?? [];
  },

  async adminGetClient(id: string): Promise<Client> {
    const response = await api.get(`/admin/clients/${id}`);
    return unwrap<Client>(response);
  },

  async adminCreateClient(data: CreateClientData): Promise<Client> {
    const response = await api.post('/admin/clients', data);
    return unwrap<Client>(response);
  },

  async adminUpdateClient(id: string, data: Partial<CreateClientData>): Promise<Client> {
    const response = await api.put(`/admin/clients/${id}`, data);
    return unwrap<Client>(response);
  },

  // C1: onboard a partir de uma empresa JÁ cadastrada (module de empresas).
  // O backend deriva o user do owner da company, garante a assinatura
  // Enterprise e faz upsert da anamnese — idempotente por company.
  async adminOnboardClient(data: CreateClientData & { companyId: string }): Promise<Client> {
    const response = await api.post('/admin/order-clients/onboard', data);
    return unwrap<{ client: Client }>(response).client;
  },

  // Clients the caller can open an order for (admin=all, client=own linked).
  async getMyClients(): Promise<Client[]> {
    const response = await api.get('/orders/my-clients');
    return unwrap<Client[]>(response) ?? [];
  },

  // ---------- Orders (client) ----------
  async getMyOrders(): Promise<Order[]> {
    const response = await api.get('/orders');
    return unwrap<Order[]>(response) ?? [];
  },

  async getOrder(id: string): Promise<Order> {
    const response = await api.get(`/orders/${id}`);
    return unwrap<Order>(response);
  },

  async createOrder(data: CreateOrderData): Promise<Order> {
    const response = await api.post('/orders', data);
    return unwrap<Order>(response);
  },

  // ---------- Intake ----------
  /**
   * COMO ENVIAR O LOTE
   *
   * O backend agrupa em itens pela PASTA quando ela vem junto — é como o cliente
   * já organiza ("cria a pasta com o SKU e joga as imagens dentro"). Sem pasta,
   * cai no SKU do nome do arquivo e, faltando isso, na visão de IA — que não é
   * reproduzível.
   *
   * O caminho vem de graça do navegador, basta não descartá-lo:
   *
   *   • `<input type="file" webkitdirectory>` → `file.webkitRelativePath`
   *   • arrastar a pasta → `DataTransferItem.webkitGetAsEntry()`
   *
   * Mandamos `paths[]` no MESMO índice de `images[]`. Arquivo solto vai com
   * string vazia, e aí o backend se comporta como antes — o campo é opcional e
   * nada muda para quem seleciona arquivos avulsos.
   */
  async uploadImages(orderId: string, files: File[]): Promise<IntakeResult> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('images[]', file);
      // `webkitRelativePath` é '' quando o arquivo não veio de uma pasta.
      formData.append('paths[]', (file as File & { webkitRelativePath?: string }).webkitRelativePath ?? '');
    });
    const response = await api.post(`/orders/${orderId}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap<IntakeResult>(response);
  },

  // ---------- Galeria do pedido ----------
  /**
   * A galeria é o destino declarado da imagem que ainda não tem grupo.
   *
   * Ela se enche sozinha no intake: quando o lote MISTURA subpastas com arquivos
   * na raiz, as subpastas viram itens e os da raiz caem aqui. Este upload é o
   * segundo caminho — o que chega depois da abertura do pedido (a foto atrasada,
   * a referência que o cliente mandou por fora).
   *
   * A régua aqui é só a técnica (imagem de verdade, formato processável, teto de
   * peso). O contrato do cliente — dimensão, proporção, peso contratado — roda na
   * PROMOÇÃO, quando a imagem passa a ser insumo de uma peça que será entregue.
   */
  async getGallery(orderId: string): Promise<OrderImage[]> {
    const response = await api.get(`/orders/${orderId}/gallery`);
    return unwrap<OrderImage[]>(response) ?? [];
  },

  async uploadToGallery(
    orderId: string,
    files: File[],
    referenceType?: ReferenceType,
  ): Promise<{ data: OrderImage[]; rejected: GalleryRejection[] }> {
    const formData = new FormData();
    files.forEach((file) => formData.append('images[]', file));
    // Sem tipo a imagem entra INERTE — é o default deliberado da galeria.
    if (referenceType) formData.append('reference_type', referenceType);
    const response = await api.post(`/orders/${orderId}/gallery`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    // `rejected` viaja FORA de `data`: são os arquivos que a porta da galeria
    // barrou, e o 201 vale para os que entraram.
    const body = response.data as { rejected?: GalleryRejection[] } | undefined;
    return {
      data: unwrap<OrderImage[]>(response) ?? [],
      rejected: body?.rejected ?? [],
    };
  },

  /**
   * Declara (ou remove) o papel da imagem no prompt. `null` a devolve ao estado
   * inerte — tira a referência da geração sem apagar o arquivo.
   */
  async setGalleryReferenceType(
    orderId: string,
    imageId: string,
    referenceType: ReferenceType | null,
  ): Promise<OrderImage> {
    const response = await api.patch(`/orders/${orderId}/gallery/${imageId}`, {
      reference_type: referenceType,
    });
    return unwrap<OrderImage>(response);
  },

  /**
   * Manda a imagem da galeria para um dos grupos do pedido.
   *
   * É aqui que a régua do cliente roda. Um 422 com `issues` não é erro de
   * sistema: é a régua informando quem tria. Reenviar com `force` promove assim
   * mesmo — a régua informa a decisão, não a substitui.
   */
  async promoteGalleryImage(
    orderId: string,
    imageId: string,
    orderItemId: string,
    force = false,
  ): Promise<OrderImage> {
    const response = await api.post(`/orders/${orderId}/gallery/${imageId}/promote`, {
      order_item_id: Number(orderItemId),
      force,
    });
    return unwrap<OrderImage>(response);
  },

  async deleteGalleryImage(orderId: string, imageId: string): Promise<void> {
    await api.delete(`/orders/${orderId}/gallery/${imageId}`);
  },

  async confirmTriage(
    orderId: string,
    items: TriageItemInput[],
    instrucoes?: string,
  ): Promise<Order> {
    // `instrucoes` (novo, TRI-04) é do PEDIDO, não do item. Só envia quando há
    // conteúdo — string vazia limparia as instruções existentes sem intenção.
    const trimmed = instrucoes?.trim();
    const payload: { items: TriageItemInput[]; instrucoes?: string } = { items };
    if (trimmed) payload.instrucoes = trimmed;
    const response = await api.post(`/orders/${orderId}/confirm-triage`, payload);
    return unwrap<Order>(response);
  },

  // ---------- Generation ----------
  async generateItem(orderId: string, itemId: string): Promise<OrderItem> {
    const response = await api.post(`/orders/${orderId}/items/${itemId}/generate-task`);
    return unwrap<OrderItem>(response);
  },
  // DEL-02: sobe uma versão retocada à mão (Photoshop) como nova versão do item.
  async attachManualVersion(orderId: string, itemId: string, file: File): Promise<OrderItem> {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post(`/orders/${orderId}/items/${itemId}/manual-version`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap<OrderItem>(response);
  },

  // Edição com IA de uma imagem de OUTPUT do pedido. Assíncrono (202): a
  // imagem editada não volta na resposta — surge como nova versão de output
  // (approval_status 'pending') no item alguns segundos depois.
  async editOrderImage(
    orderId: string,
    itemId: string,
    orderImageId: string,
    prompt: string,
    opts?: { imageSize?: '1K' | '2K' | '4K'; size?: string; outputFormat?: 'png' | 'jpeg' },
  ): Promise<OrderItem> {
    const response = await api.post(
      `/orders/${orderId}/items/${itemId}/images/${orderImageId}/edit`,
      {
        prompt,
        ...(opts?.imageSize ? { imageSize: opts.imageSize } : {}),
        ...(opts?.size ? { size: opts.size } : {}),
        ...(opts?.outputFormat ? { outputFormat: opts.outputFormat } : {}),
      },
    );
    return unwrap<OrderItem>(response);
  },

  // Admin-only: mescla 2+ itens em 1 só (a IA às vezes separa um conjunto em
  // vários itens). O primeiro id do array é o alvo/sobrevivente — a ordem
  // importa. `itemIds` chega aqui em camelCase; o interceptor da api.ts
  // decameliza o corpo pra `item_ids` antes de sair pra rede.
  async adminMergeOrderItems(orderId: string, itemIds: string[], name?: string): Promise<OrderItem> {
    const payload: { itemIds: string[]; name?: string } = { itemIds };
    if (name) payload.name = name;
    const response = await api.post(`/orders/${orderId}/items/merge`, payload);
    return unwrap<OrderItem>(response);
  },

  // ---------- W2: workflow editor source + order-scoped generation ----------
  // Items of an order exposed to the workflow editor (admin), each flagged eligible
  // for generation. Envelope {data} → unwrap extracts it.
  async getWorkflowSource(orderId: number): Promise<WorkflowSource> {
    const response = await api.get(`/orders/${orderId}/workflow-source`);
    return unwrap<WorkflowSource>(response);
  },

  // Fire one order-scoped generation for a single item, driven by a SAVED workflow.
  // `workflowRunId` is one uuid per "Executar Tudo" run (groups the item generations).
  async workflowGenerateTask(
    orderId: number,
    itemId: number,
    payload: {
      workflowId: number;
      workflowRunId: string;
      promptExtra?: string;
      expectedOutputs?: number;
      n?: number;
      size?: string;
      quality?: string;
      imageSize?: string;
      outputFormat?: string;
      provider?: string;
      customModelId?: number;
      modelDescription?: string;
      /** Default no backend é `true`; só vale mandar `false` para dispensar. */
      useGalleryReferences?: boolean;
    },
  ): Promise<WorkflowGenerateTaskResponse> {
    const response = await api.post(`/orders/${orderId}/items/${itemId}/workflow-generate-task`, payload);
    return unwrap<WorkflowGenerateTaskResponse>(response);
  },

  // ---------- Delivery / approval ----------
  async addAnnotations(
    orderId: string,
    itemId: string,
    imageId: string,
    annotations: ImageAnnotation[],
  ): Promise<OrderItem> {
    const response = await api.post(
      `/orders/${orderId}/items/${itemId}/images/${imageId}/annotations`,
      { annotations },
    );
    return unwrap<OrderItem>(response);
  },

  async approveImage(orderId: string, itemId: string, imageId: string): Promise<OrderItem> {
    const response = await api.post(
      `/orders/${orderId}/items/${itemId}/images/${imageId}/approve`,
    );
    return unwrap<OrderItem>(response);
  },

  async rejectImage(
    orderId: string,
    itemId: string,
    imageId: string,
    annotations?: ImageAnnotation[],
  ): Promise<OrderItem> {
    const response = await api.post(
      `/orders/${orderId}/items/${itemId}/images/${imageId}/reject`,
      annotations ? { annotations } : undefined,
    );
    return unwrap<OrderItem>(response);
  },

  async approveAll(orderId: string): Promise<Order> {
    const response = await api.post(`/orders/${orderId}/approve-all`);
    return unwrap<Order>(response);
  },

  // Download the standardised delivery zip through the authenticated client
  // (a plain <a>/window.open would not carry the Bearer token), returning the
  // binary Blob for the caller to save.
  async downloadZip(orderId: string): Promise<Blob> {
    const response = await api.get(`/orders/${orderId}/download`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  // ---------- Geração em lote (A+B) ----------
  async getGenerationEstimate(
    orderId: string,
    scope: 'all' | 'selected',
    itemIds?: string[],
    workflowId?: number,
  ): Promise<GenerationEstimate> {
    const response = await api.get(`/orders/${orderId}/generation-estimate`, {
      params: humps.decamelizeKeys({
        scope,
        itemIds: itemIds ?? [],
        ...(workflowId ? { workflowId } : {}),
      }),
    });
    return unwrap<GenerationEstimate>(response);
  },

  // Retorna 202 com o resumo (queued_items/skipped/estimate). Em 422 (saldo
  // insuficiente / nada elegível / workflow inválido) o api.ts lança — o
  // chamador lê err.response.
  async generateBatch(
    orderId: string,
    data: {
      scope: 'all' | 'selected';
      itemIds?: string[];
      workflowId?: number;
      setClientDefault?: boolean;
      /** Leva 2: override auditável — gera itens com reenvio aberto usando só as imagens disponíveis. */
      forceIncompleteItemIds?: string[];
    },
  ): Promise<{ queuedItems: number; estimate: GenerationEstimate }> {
    const response = await api.post(`/orders/${orderId}/generate-batch`, data);
    return unwrap<{ queuedItems: number; estimate: GenerationEstimate }>(response);
  },

  // ---------- Intake Leva 2: reenvio endereçado ----------
  async getResubmissionRequests(orderId: string): Promise<ResubmissionRequest[]> {
    const response = await api.get(`/orders/${orderId}/resubmission-requests`);
    return unwrap<ResubmissionRequest[]>(response) ?? [];
  },

  // Admin abre uma solicitação endereçando imagens específicas com motivo.
  async createResubmissionRequest(
    orderId: string,
    lines: { imageId: string; reason: string }[],
  ): Promise<ResubmissionRequest> {
    const response = await api.post(`/orders/${orderId}/resubmission-requests`, { lines });
    return unwrap<ResubmissionRequest>(response);
  },

  async cancelResubmissionLine(orderId: string, lineId: string): Promise<ResubmissionLine> {
    const response = await api.post(`/orders/${orderId}/resubmission-lines/${lineId}/cancel`);
    return unwrap<ResubmissionLine>(response);
  },

  async acceptResubmissionLine(orderId: string, lineId: string): Promise<ResubmissionLine> {
    const response = await api.post(`/orders/${orderId}/resubmission-lines/${lineId}/accept`);
    return unwrap<ResubmissionLine>(response);
  },

  // Reenvio do cliente endereçado pela LINE (nunca por nome de arquivo).
  // clientUploadId garante idempotência: replay devolve o mesmo replacement.
  // Recusa determinística volta 200 com rejected=true (não é erro HTTP).
  async uploadResubmissionReplacement(
    orderId: string,
    lineId: string,
    file: File,
    clientUploadId: string,
  ): Promise<ResubmissionUploadResult> {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('client_upload_id', clientUploadId);
    const response = await api.post(
      `/orders/${orderId}/resubmission-lines/${lineId}/upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data as ResubmissionUploadResult;
  },

  // ---------- Kanban (admin) ----------
  async adminGetOrders(filters?: AdminOrderFilters): Promise<Order[]> {
    // Query params bypass the api client's camel→snake conversion (it only
    // transforms request bodies), so decamelize them here or the backend
    // silently ignores clientId/assigneeId/needsReview filters.
    const response = await api.get('/admin/orders', {
      params: humps.decamelizeKeys(filters ?? {}),
    });
    return unwrap<Order[]>(response) ?? [];
  },

  async adminUpdateOrder(id: string, data: AdminUpdateOrderData): Promise<Order> {
    const response = await api.patch(`/admin/orders/${id}`, data);
    return unwrap<Order>(response);
  },

  // G3: status-move timeline of an order (admin-scoped so operator names and
  // internal reasons never leak to clients).
  async adminGetOrderHistory(id: string): Promise<OrderStatusHistoryEntry[]> {
    const response = await api.get(`/admin/orders/${id}/history`);
    return unwrap<OrderStatusHistoryEntry[]>(response) ?? [];
  },

  // Admin-only: running tally of Snapfy credits this order consumed across every
  // generation path (batch, workflow, regeneration, edits). Internal cost — the
  // route is admin-guarded so it never reaches clients.
  async adminGetOrderCost(id: string): Promise<OrderCost> {
    const response = await api.get(`/admin/orders/${id}/cost`);
    return unwrap<OrderCost>(response);
  },

  // Soft delete auditado — remove o pedido de todas as listagens (cliente e
  // admin); itens/imagens/histórico permanecem no banco. Backend retorna 204.
  async adminDeleteOrder(id: string): Promise<void> {
    await api.delete(`/admin/orders/${id}`);
  },
};

export default OrderService;
